import React, { useState, Component } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Header } from './components/layout/Header';
import { HomePage } from './pages/HomePage';
import { PoemLibrary } from './pages/PoemLibrary';
import { ParentGuide } from './pages/ParentGuide';
import { PoemSelector } from './components/shared/PoemSelector';
import { RhythmGame } from './components/modules/RhythmGame';
import { ScaleExplorer } from './components/modules/ScaleExplorer';
import { BodyPercussion } from './components/modules/BodyPercussion';
import { ListenModule } from './components/modules/ListenModule';
import { ModesExplorer } from './components/modules/ModesExplorer';
import { SingAlong } from './components/modules/SingAlong';
import { StaffNotation } from './components/modules/StaffNotation';
import { MelodyDrawing } from './components/modules/MelodyDrawing';
import { TempoExplorer } from './components/modules/TempoExplorer';
import { POEMS, MODULES } from './data/mockData';
import { Poem } from './types';
import { playClick } from './utils/audioUtils';
export function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null);
  const [selectedPoem, setSelectedPoem] = useState<Poem>(POEMS[0]);
  const handleNavigate = (page: string) => {
    setCurrentPage(page);
    if (page === 'home' || page === 'poems' || page === 'guide') {
      setActiveModuleId(null);
    }
  };
  const handleModuleSelect = (id: string) => {
    setActiveModuleId(id);
    setCurrentPage('module');
  };
  const handlePoemSelect = (poem: Poem) => {
    setSelectedPoem(poem);
    setActiveModuleId('rhythm');
    setCurrentPage('module');
  };
  const getActiveModuleComponent = () => {
    switch (activeModuleId) {
      case 'rhythm':
        return <RhythmGame poem={selectedPoem} data-id="element-0" />;
      case 'scale':
        return <ScaleExplorer data-id="element-1" />;
      case 'body':
        return <BodyPercussion data-id="element-2" />;
      case 'listen':
        return <ListenModule poem={selectedPoem} data-id="element-3" />;
      case 'modes':
        return <ModesExplorer data-id="element-4" />;
      case 'sing':
        return <SingAlong poem={selectedPoem} data-id="element-5" />;
      case 'staff':
        return <StaffNotation data-id="element-6" />;
      case 'melody':
        return <MelodyDrawing data-id="element-7" />;
      case 'tempo':
        return <TempoExplorer data-id="element-8" />;
      default:
        return <div className="flex flex-col items-center justify-center h-64 text-gray-500 bg-white rounded-3xl p-12 shadow-sm" data-id="element-9">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-10">
              <span className="text-2xl" data-id="element-11">🚧</span>
            </div>
            <p className="text-xl font-bold text-gray-800" data-id="element-12">
              此功能正在开发中...
            </p>
            <p className="mt-2 text-gray-500" data-id="element-13">Coming Soon</p>
          </div>;
    }
  };
  const activeModuleDef = MODULES.find(m => m.id === activeModuleId);
  return <div className="min-h-screen bg-gray-50 font-sans" data-id="element-14">
      <Header onNavigate={handleNavigate} currentPage={currentPage} data-id="element-15" />

      <main className="pb-20" data-id="element-16">
        {currentPage === 'home' && <HomePage onModuleSelect={handleModuleSelect} data-id="element-17" />}

        {currentPage === 'poems' && <PoemLibrary onSelectPoem={handlePoemSelect} data-id="element-18" />}

        {currentPage === 'guide' && <ParentGuide data-id="element-19" />}

        {currentPage === 'module' && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-20">
            {/* Module Header */}
            <div className="mb-8" data-id="element-21">
              <button onClick={() => {
            playClick();
            handleNavigate('home');
          }} className="flex items-center text-gray-500 hover:text-gray-900 transition-colors mb-4 group" data-id="element-22">
                <div className="p-1 rounded-full bg-gray-100 group-hover:bg-gray-200 mr-2 transition-colors" data-id="element-23">
                  <ArrowLeft className="w-4 h-4" data-id="element-24" />
                </div>
                返回实验室
              </button>

              <div className="flex items-center space-x-4" data-id="element-25">
                <div className="p-3 rounded-2xl text-white shadow-lg" style={{
              backgroundColor: activeModuleDef?.color
            }} data-id="element-26">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center" data-id="element-27">
                    <span className="font-bold text-lg" data-id="element-28">♪</span>
                  </div>
                </div>
                <div data-id="element-29">
                  <h1 className="text-3xl font-bold text-gray-900" data-id="element-30">
                    {activeModuleDef?.title}
                  </h1>
                  <p className="text-gray-500" data-id="element-31">
                    {activeModuleDef?.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Poem Selector (only for relevant modules) */}
            {['rhythm', 'listen', 'sing'].includes(activeModuleId || '') && <PoemSelector poems={POEMS} selectedPoem={selectedPoem} onSelect={setSelectedPoem} data-id="element-32" />}

            {/* Active Module Content */}
            <div className="mt-8 transition-all duration-500 ease-in-out" data-id="element-33">
              {getActiveModuleComponent()}
            </div>
          </div>}
      </main>

      {/* Simple Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-auto" data-id="element-34">
        <div className="max-w-7xl mx-auto px-4 text-center text-gray-400 text-sm" data-id="element-35">
          <p data-id="element-36">© 2026《同唱古诗词》音乐实验室 Ancient Poetry Music Lab</p>
          <p className="mt-2" data-id="element-37">让每个孩子都爱上中国传统文化</p>
        </div>
      </footer>
    </div>;
}